import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { OrderDirection, TextFilterResponse } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { TextService } from '../../Services/TextService';
import { Utils } from '../../Services/Utils';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';
import EditButton from '../Buttons/EditButton';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import LanguageBadgeList from '../Language/LanguageBadgeList';

const TextTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Texty');
    }, []);

    const { filterTexts } = TextService();
    const [response, setResponse] = useState<TextFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.text;
    const { pageId } = useParams<{ pageId: string }>();

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        const limit = opts?.perPage ?? perPage;
        const resp = await filterTexts({
            websiteId: currentSite.id,
            limit,
            pageId: pageId ? Number(pageId) : undefined,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite, pageId]);

    return (
        <TableShell
            title="Texty"
            icon={icon}
            accentColor={action}
            backHref="/page/table"
            isLoading={response === null}
            isEmpty={response !== null && response.data.length === 0}
            emptyMessage="Žiadne texty"
            page={page}
            totalCount={response?.totalCount}
            perPage={perPage}
            onPageChange={p => { setPage(p); loadPage(p); }}
            toolbar={
                <TableToolbar
                    totalCount={response?.totalCount ?? 0}
                    nounForms={['text', 'texty', 'textov']}
                    perPage={perPage}
                    onPerPageChange={changePerPage}
                    orderDirection={orderDirection}
                    onOrderChange={changeOrder}
                />
            }
        >
            <Table responsive>
                <thead>
                    <tr>
                        <th>Kód</th>
                        <th>Ukážka textu (default jazyk)</th>
                        <th>Jazyky</th>
                        <th className="w-1 text-center">Akcie</th>
                    </tr>
                </thead>
                <tbody>
                    {response?.data.map(text => {
                        let defaultTranslation = text.translations.find(t => t.isDefault);

                        return (
                            <tr key={text.id}>
                                <td><b>{text.code}</b></td>
                                <td
                                    style={{ color: '#64748B', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    dangerouslySetInnerHTML={{
                                        __html: defaultTranslation ? Utils.truncateString(defaultTranslation.value, 300) : '',
                                    }}
                                />
                                <td>
                                    <LanguageBadgeList languages={text.translations} accentColor={action} />
                                </td>
                                <td className="text-center">
                                    <CustomTooltip placement="right" message="Upraviť">
                                        <Link to={`/text/edit/${text.id}`}>
                                            <EditButton color={action} iconOnly />
                                        </Link>
                                    </CustomTooltip>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </TableShell>
    );
};

export default TextTableComponent;
